import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Building, Plus, Trash2, Edit, Users, UserCog, Shield } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Aziende = () => {
  const { isSindacatoAdmin } = useAuth();
  const canManage = isSindacatoAdmin();
  const [aziende, setAziende] = useState([]);
  const [direttori, setDirettori] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedAzienda, setSelectedAzienda] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    descrizione: '',
    tipo: 'PRIVATA',
    direttore_id: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [aziendeRes, staffRes] = await Promise.all([
        axios.get(`${API_URL}/aziende`),
        axios.get(`${API_URL}/staff`)
      ]);
      setAziende(aziendeRes.data);
      setDirettori(staffRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/aziende`, {
        ...formData,
        direttore_id: formData.direttore_id === 'none' ? null : formData.direttore_id || null
      });
      toast.success('Azienda creata con successo');
      setIsDialogOpen(false);
      setFormData({ nome: '', descrizione: '', tipo: 'PRIVATA', direttore_id: '' });
      fetchData();
    } catch (error) {
      toast.error('Errore nella creazione');
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(`${API_URL}/aziende/${selectedAzienda.id}`, {
        nome: formData.nome,
        descrizione: formData.descrizione,
        tipo: formData.tipo,
        direttore_id: formData.direttore_id === 'none' ? null : formData.direttore_id || null
      });
      toast.success('Azienda aggiornata');
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa azienda?')) return;
    try {
      await axios.delete(`${API_URL}/aziende/${id}`);
      toast.success('Azienda eliminata');
      fetchData();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const openEditDialog = (azienda) => {
    setSelectedAzienda(azienda);
    setFormData({
      nome: azienda.nome,
      descrizione: azienda.descrizione,
      tipo: azienda.tipo,
      direttore_id: azienda.direttore_id || ''
    });
    setEditDialogOpen(true);
  };

  const tipiAzienda = ['PUBBLICA', 'PRIVATA', 'SINDACATO', 'MUNICIPIO', 'OSPEDALE', 'POLIZIA', 'ALTRO'];

  const getTipoBadge = (tipo) => {
    const colors = {
      'PUBBLICA': 'bg-blue-100 text-blue-800',
      'PRIVATA': 'bg-green-100 text-green-800',
      'SINDACATO': 'bg-purple-100 text-purple-800',
      'MUNICIPIO': 'bg-amber-100 text-amber-800',
      'OSPEDALE': 'bg-red-100 text-red-800',
      'POLIZIA': 'bg-slate-100 text-slate-800',
      'ALTRO': 'bg-gray-100 text-gray-800'
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-semibold rounded-full ${colors[tipo] || colors['ALTRO']}`}>
        {tipo}
      </span>
    );
  };

  return (
    <div data-testid="aziende-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-header flex items-center gap-3">
            <Building className="w-8 h-8" />
            Gestione Aziende
          </h1>
          <p className="text-slate-600 mt-1">Crea e gestisci le aziende di Nova Roleplay</p>
        </div>
        {canManage && (
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-azienda-btn" className="bg-slate-900 hover:bg-slate-800 rounded-sm">
                <Plus className="w-4 h-4 mr-2" />
                Nuova Azienda
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-serif text-xl">Crea Nuova Azienda</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="nome">Nome Azienda</Label>
                  <Input
                    id="nome"
                    data-testid="azienda-nome-input"
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    placeholder="es. Municipio di Nova"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tipo</Label>
                  <Select
                    value={formData.tipo}
                    onValueChange={(value) => setFormData({ ...formData, tipo: value })}
                  >
                    <SelectTrigger data-testid="azienda-tipo-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {tipiAzienda.map((tipo) => (
                        <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="descrizione">Descrizione</Label>
                  <Textarea
                    id="descrizione"
                    data-testid="azienda-desc-input"
                    value={formData.descrizione}
                    onChange={(e) => setFormData({ ...formData, descrizione: e.target.value })}
                    placeholder="Descrizione dell'azienda..."
                    rows={3}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Direttore Responsabile</Label>
                  <Select
                    value={formData.direttore_id}
                    onValueChange={(value) => setFormData({ ...formData, direttore_id: value })}
                  >
                    <SelectTrigger data-testid="azienda-direttore-select">
                      <SelectValue placeholder="Seleziona un direttore" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nessun direttore</SelectItem>
                      {direttori.map((d) => (
                        <SelectItem key={d.id} value={d.id}>{d.nome}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Annulla
                  </Button>
                  <Button type="submit" data-testid="submit-azienda-btn" className="bg-slate-900 hover:bg-slate-800">
                    Crea Azienda
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Aziende Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
        </div>
      ) : aziende.length === 0 ? (
        <Card className="border-dashed border-2">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Building className="w-12 h-12 text-slate-300 mb-4" />
            <p className="text-slate-500">Nessuna azienda creata</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {aziende.map((azienda) => (
            <Card key={azienda.id} data-testid={`azienda-card-${azienda.id}`} className="border border-slate-200 hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    {getTipoBadge(azienda.tipo)}
                    <CardTitle className="font-serif text-lg mt-2">{azienda.nome}</CardTitle>
                  </div>
                  {canManage && (
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid={`edit-azienda-${azienda.id}`}
                        onClick={() => openEditDialog(azienda)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid={`delete-azienda-${azienda.id}`}
                        onClick={() => handleDelete(azienda.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600 mb-4 line-clamp-2">{azienda.descrizione}</p>
                <div className="space-y-2 pt-3 border-t border-slate-100">
                  <div className="flex items-center gap-2 text-sm">
                    <Shield className="w-4 h-4 text-amber-600" />
                    <span className="text-slate-600">Direttore:</span>
                    <span className="font-medium">{azienda.direttore_nome || 'Non assegnato'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Users className="w-4 h-4 text-blue-600" />
                    <span className="text-slate-600">Dipendenti:</span>
                    <span className="font-medium">{azienda.dipendenti_count}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Modifica Azienda</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="edit-nome">Nome Azienda</Label>
              <Input
                id="edit-nome"
                data-testid="edit-azienda-nome"
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Tipo</Label>
              <Select
                value={formData.tipo}
                onValueChange={(value) => setFormData({ ...formData, tipo: value })}
              >
                <SelectTrigger data-testid="edit-azienda-tipo">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {tipiAzienda.map((tipo) => (
                    <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-descrizione">Descrizione</Label>
              <Textarea
                id="edit-descrizione"
                data-testid="edit-azienda-desc"
                value={formData.descrizione}
                onChange={(e) => setFormData({ ...formData, descrizione: e.target.value })}
                rows={3}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Direttore Responsabile</Label>
              <Select
                value={formData.direttore_id}
                onValueChange={(value) => setFormData({ ...formData, direttore_id: value })}
              >
                <SelectTrigger data-testid="edit-azienda-direttore">
                  <SelectValue placeholder="Seleziona un direttore" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessun direttore</SelectItem>
                  {direttori.map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Annulla
              </Button>
              <Button type="submit" data-testid="save-azienda-btn" className="bg-slate-900 hover:bg-slate-800">
                Salva Modifiche
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Aziende;
